import React, { useState } from "react";
import { authorize, login } from "../services/api";
import { toast } from "react-toastify";
import Logo from "../assets/logo-old.png";
import EyeIcon from "../assets/eye-icon.svg";
import EyeOffIcon from "../assets/eyeoff-icon.svg";
import { useNavigate } from "react-router-dom";
import AnnouncementModal from "../components/AnnouncementModal";

interface FormData {
  email: string;
  password: string;
}

interface Errors {
  email: string;
  password: string;
}

const Login: React.FC = () => {
  const [formData, setFormData] = useState<FormData>({
    email: "",
    password: "",
  });
  const [errors, setErrors] = useState<Errors>({ email: "", password: "" });
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [isAnnouncementOpen, setIsAnnouncementOpen] = useState<boolean>(
    process.env.REACT_APP_IS_ANNOUNCEMENT === "true",
  );
  const navigate = useNavigate();

  const toggleShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    setErrors({ ...errors, [name]: "" });
  };

  const validate = (): boolean => {
    let valid = true;
    let newErrors: Errors = { email: "", password: "" };

    if (!formData.email) {
      newErrors.email = "Email is required.";
      valid = false;
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "Invalid email format.";
      valid = false;
    }

    if (!formData.password) {
      newErrors.password = "Password is required.";
      valid = false;
    } else if (formData.password.length < 6) {
      newErrors.password = "Password must be at least 6 characters.";
      valid = false;
    }

    setErrors(newErrors);
    return valid;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validate()) return;

    setIsSubmitting(true);

    try {
      const data = (await login(formData.email, formData.password)) as any;
      if (data && data.data) {
        const token = data.data.token;
        const user = data.data.user;
        const authData = (await authorize(token, user.email)) as any;
        if (authData && authData.data && authData.data.token != null) {
          const authToken = authData.data.token;
          localStorage.setItem("user", JSON.stringify(user));
          if (authToken.length > 1) {
            navigate(`/auth?code=${token}&username=${user.email}`);
          } else {
            const authToken = authData.data.token[0];
            toast.success("Login successful");
            localStorage.setItem("token", token);
            localStorage.setItem("baseUrl", authToken.base_url);
            localStorage.setItem("business_id", authToken.business_id);
            localStorage.setItem("session", authToken.session_id);
            localStorage.setItem("accessToken", authToken.access_token);
            navigate("/");
          }
        } else {
          toast.error("Authorization failed");
          setIsSubmitting(false);
          return;
        }
      } else {
        toast.error(data?.message || "Login failed.");
        setIsSubmitting(false);
      }
    } catch (error: any) {
      toast.error(error?.message || "An error occurred. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      {isAnnouncementOpen && (
        <AnnouncementModal onClose={() => setIsAnnouncementOpen(false)} />
      )}
      <div className="flex items-center justify-center min-h-screen bg-white">
        <div className="max-w-md w-full p-6 bg-white shadow-lg rounded-lg">
          <div className="text-center mb-6">
            <img src={Logo} alt="Logo" className="mx-auto mb-4 h-16" />
            <h1 className="text-2xl font-bold">Login</h1>
          </div>
          <form onSubmit={handleSubmit}>
            <div className="mb-4">
              <label
                htmlFor="email"
                className="block text-sm font-medium text-gray-700"
              >
                Email
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className={`mt-1 block w-full rounded-md p-4 border border-solid  border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm ${
                  errors.email ? "border-red-500" : ""
                }`}
              />
              {errors.email && (
                <p className="mt-1 text-xs text-red-500">{errors.email}</p>
              )}
            </div>
            <div className="mb-4 relative">
              <label
                htmlFor="password"
                className="block text-sm font-medium text-gray-700"
              >
                Password
              </label>
              <input
                id="password"
                type={showPassword ? "text" : "password"}
                name="password"
                value={formData.password}
                onChange={handleChange}
                className={`mt-1 block w-full p-4 rounded-md border border-solid border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm ${
                  errors.password ? "border-red-500" : ""
                }`}
              />
              <div
                className="absolute top-[24px] bottom-[0px] right-3 flex items-center cursor-pointer"
                onClick={toggleShowPassword}
              >
                {showPassword ? (
                  <img
                    src={EyeOffIcon}
                    alt="eyeofficon"
                    className="h-5 w-5 text-gray-500"
                  />
                ) : (
                  <img
                    src={EyeIcon}
                    alt="eyeicon"
                    className="h-5 w-5 text-gray-500"
                  />
                )}
              </div>
              {errors.password && (
                <p className="mt-1 text-xs text-red-500">{errors.password}</p>
              )}
            </div>
            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-blue-500 text-white py-2 px-4 rounded-md hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50"
            >
              {isSubmitting ? "Logging in..." : "Login"}
            </button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-sm text-gray-600">
              Don't have an account?{" "}
              <a
                href="https://client.maksitech.id/auth/register"
                className="text-blue-500 hover:underline"
              >
                Create a new account
              </a>
            </p>
          </div>

          <div className="mt-6 text-center">
            <p className="text-sm text-gray-600">
              Powered by PT. Maksi Media Indonesia ©2025
            </p>
          </div>
        </div>
      </div>
    </>
  );
};

export default Login;
